import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useToken from 'hooks/useToken';
import useVacationLetterPDF from 'hooks/useVacationLetterPDF';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { Badge, Button, Col, Form, Image, Modal, Row, Spinner, ListGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const VacationDetailsModal = ({
    show,
    onHide,
    employee,
    onSuccess,
    showActions = true
}) => {
    const { token, idCatTipoUsuario } = useToken();
    const { generatePDF, isGenerating } = useVacationLetterPDF();
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [procesando, setProcesando] = useState(false);
    const [imagenPerfil, setImagenPerfil] = useState('');
    const [cargandoImagen, setCargandoImagen] = useState(false);

    /**
     * Cargar imagen del perfil del empleado
     */
    useEffect(() => {
        const cargarImagenPerfil = async () => {
            if (!employee?.idUsuario) {
                setImagenPerfil('');
                return;
            }

            setCargandoImagen(true);
            try {
                const response = await fetch(`/api/perfil/obtenerImagenPerfil/?idUsuario=${employee.idUsuario}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'text/plain'
                    }
                });

                if (response.ok) {
                    const data = await response.text();
                    setImagenPerfil(data);
                } else {
                    console.error('Error al cargar imagen:', response.status);
                    setImagenPerfil('');
                }
            } catch (error) {
                console.error('Error al cargar imagen del perfil:', error);
                setImagenPerfil('');
            } finally {
                setCargandoImagen(false);
            }
        };

        if (show && employee) {
            cargarImagenPerfil();
        }
    }, [employee?.idUsuario, show, token]);

    /**
     * Parsear y formatear las fechas específicas del JSON
     * IMPORTANTE: Este hook debe ejecutarse SIEMPRE, antes de cualquier return condicional
     */
    const fechasEspecificas = useMemo(() => {
        if (!employee || !employee.vFechasJson) return null;

        try {
            const fechas = JSON.parse(employee.vFechasJson);
            if (!Array.isArray(fechas) || fechas.length === 0) return null;

            // Convertir y formatear cada fecha
            return fechas.map(fechaStr => {
                const fecha = new Date(fechaStr + 'T00:00:00');
                return {
                    dateString: fechaStr,
                    formatted: fecha.toLocaleDateString('es-ES', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    }),
                    isWeekend: fecha.getDay() === 0 || fecha.getDay() === 6
                };
            });
        } catch (error) {
            console.error('Error al parsear vFechasJson:', error);
            return null;
        }
    }, [employee?.vFechasJson]);

    // AHORA sí podemos hacer el return condicional, DESPUÉS de todos los hooks
    if (!employee) return null;

    const getStatusBadge = (estado) => {
        const estados = {
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'APROBADA': { bg: 'success', text: 'Aprobada' },
            'RECHAZADA': { bg: 'danger', text: 'Rechazada' }
        };

        const status = estados[estado?.toUpperCase()] || { bg: 'secondary', text: estado };
        return <Badge bg={status.bg}>{status.text}</Badge>;
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'Fecha no disponible';

        try {
            if (dateValue instanceof Date && !isNaN(dateValue)) {
                return dateValue.toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            const date = new Date(dateValue);
            if (!isNaN(date)) {
                return date.toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }

            return 'Fecha no válida';
        } catch (e) {
            console.error('Error al formatear fecha:', e);
            return 'Error en fecha';
        }
    };

    const handleGeneratePDF = () => {
        try {
            const vacationData = {
                employeeName: employee.name,
                startDate: employee.vacationStart || employee.dFechaInicio,
                endDate: employee.vacationEnd || employee.dFechaFin,
                totalDays: employee.daysRequested || employee.iDiasSolicitados || 0,
                specificDates: fechasEspecificas
            };

            generatePDF(vacationData);
        } catch (error) {
            alert(error.message);
        }
    };

    const handleAprobar = async () => {
        setProcesando(true);
        try {
            const response = await fetch('/api/vacaciones/AprobarVacaciones', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ idSolicitud: employee.idVacacion })
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                if (onSuccess) {
                    onSuccess();
                }
                onHide();
            } else {
                alert('Error: ' + data.vMessage);
            }
        } catch (error) {
            console.error('Error al aprobar vacaciones:', error);
            alert('Error al aprobar vacaciones');
        } finally {
            setProcesando(false);
        }
    };

    const handleRechazar = () => {
        setShowRejectModal(true);
    };

    const confirmarRechazo = async () => {
        if (!motivoRechazo.trim()) {
            alert('Debe ingresar el motivo del rechazo');
            return;
        }

        setProcesando(true);
        try {
            const response = await fetch('/api/vacaciones/RechazarVacaciones', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idSolicitud: employee.idVacacion,
                    motivo: motivoRechazo
                })
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                setShowRejectModal(false);
                setMotivoRechazo('');
                if (onSuccess) {
                    onSuccess();
                }
                onHide();
            } else {
                alert('Error: ' + data.vMessage);
            }
        } catch (error) {
            console.error('Error al rechazar vacaciones:', error);
            alert('Error al rechazar vacaciones');
        } finally {
            setProcesando(false);
        }
    };

    const isPending = employee.estado?.toUpperCase() === 'PENDIENTE';
    const isAdmin = idCatTipoUsuario === 1 || idCatTipoUsuario === '1';
    const shouldShowActionButtons = showActions && isAdmin && isPending;

    // Determinar la imagen a mostrar
    const imageToDisplay = imagenPerfil || employee.photo || '/img/team/nofoto50.png';

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Detalles de Vacaciones</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col xs={12} className="text-center mb-3">
                            {cargandoImagen ? (
                                <div style={{ width: '100px', height: '100px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Spinner animation="border" variant="primary" />
                                </div>
                            ) : (
                                <Image
                                    src={imageToDisplay}
                                    roundedCircle
                                    width={100}
                                    height={100}
                                    className="border border-2"
                                    style={{ objectFit: 'cover' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/img/team/nofoto50.png";
                                    }}
                                />
                            )}
                        </Col>
                        <Col xs={12}>
                            <div className="text-center mb-3">
                                <h5 className="mb-1">
                                    {employee.idUsuario ? (
                                        <Link
                                            to={`/perfil/${employee.idUsuario}`}
                                            style={{ textDecoration: 'none', color: 'inherit' }}
                                            onClick={onHide}
                                        >
                                            {employee.name}
                                        </Link>
                                    ) : (
                                        employee.name
                                    )}
                                </h5>
                                <p className="text-primary mb-1">{employee.position}</p>
                                {getStatusBadge(employee.estado)}
                            </div>
                            <div className="bg-light rounded p-3 border">
                                <Row className="g-2">
                                    <Col xs={12}>
                                        <strong>Departamento:</strong>
                                        <span className="ms-2">{employee.department}</span>
                                    </Col>
                                    <Col xs={12}>
                                        <strong>Categor&iacute;a:</strong>
                                        <span className="ms-2">{employee.categoria || 'No especificado'}</span>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="mt-2 p-2 bg-white rounded border">
                                            <h6 className="mb-2">
                                                <FontAwesomeIcon icon="calendar-alt" className="me-2 text-primary" />
                                                Periodo Solicitado
                                            </h6>
                                            <p className="mb-1">
                                                <strong>Inicio:</strong>{' '}
                                                {formatDate(employee.vacationStart || employee.dFechaInicio)}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Fin:</strong>{' '}
                                                {formatDate(employee.vacationEnd || employee.dFechaFin)}
                                            </p>
                                            <p className="mb-0">
                                                <strong>Total d&iacute;as:</strong>{' '}
                                                <Badge bg="primary" pill>
                                                    {employee.daysRequested || employee.iDiasSolicitados || 0}
                                                </Badge>
                                            </p>
                                        </div>
                                    </Col>

                                    {/* Mostrar fechas específicas si existen */}
                                    {fechasEspecificas && fechasEspecificas.length > 0 && (
                                        <Col xs={12}>
                                            <div className="mt-2 p-3 bg-white rounded border">
                                                <h6 className="mb-2">
                                                    <FontAwesomeIcon icon="calendar-check" className="me-2 text-success" />
                                                    D&iacute;as Espec&iacute;ficos Solicitados
                                                    <Badge bg="info" pill className="ms-2">
                                                        {fechasEspecificas.length}
                                                    </Badge>
                                                </h6>
                                                <div 
                                                    style={{ 
                                                        maxHeight: '200px', 
                                                        overflowY: 'auto',
                                                        overflowX: 'hidden'
                                                    }}
                                                >
                                                    <ListGroup variant="flush">
                                                        {fechasEspecificas.map((fecha, index) => (
                                                            <ListGroup.Item 
                                                                key={fecha.dateString}
                                                                className="d-flex justify-content-between align-items-center py-2 px-2"
                                                                style={{ backgroundColor: 'transparent', border: 'none' }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <Badge 
                                                                        bg={fecha.isWeekend ? 'secondary' : 'light'} 
                                                                        text={fecha.isWeekend ? 'white' : 'dark'}
                                                                        className="me-2"
                                                                        style={{ minWidth: '30px' }}
                                                                    >
                                                                        {index + 1}
                                                                    </Badge>
                                                                    <span className="small text-capitalize">
                                                                        {fecha.formatted}
                                                                        {fecha.isWeekend && (
                                                                            <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                                                                Fin de semana
                                                                            </Badge>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </ListGroup.Item>
                                                        ))}
                                                    </ListGroup>
                                                </div>
                                                <small className="text-muted d-block mt-2">
                                                    <FontAwesomeIcon icon="info-circle" className="me-1" />
                                                    Estos son los d&iacute;as exactos que el empleado ha solicitado
                                                </small>
                                            </div>
                                        </Col>
                                    )}

                                    {/* Mostrar comentarios si existen y el estado es RECHAZADA */}
                                    {employee.estado?.toUpperCase() === 'RECHAZADA' && employee.comentarios && (
                                        <Col xs={12}>
                                            <div className="mt-2 p-3 bg-danger bg-opacity-10 rounded border border-danger">
                                                <h6 className="mb-2 text-danger">
                                                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                                                    Motivo del Rechazo
                                                </h6>
                                                <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                                                    {employee.comentarios}
                                                </p>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    {/* Botón de descargar PDF a la izquierda */}
                    <Button
                        variant="outline-danger"
                        onClick={handleGeneratePDF}
                        disabled={isGenerating || procesando}
                        title="Descargar Carta de Solicitud PDF"
                    >
                        {isGenerating ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Generando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="file-pdf" className="me-2" />
                                Descargar PDF
                            </>
                        )}
                    </Button>

                    {/* Botones de acción a la derecha */}
                    <div>
                        {shouldShowActionButtons ? (
                            <>
                                <Button
                                    variant="secondary"
                                    onClick={onHide}
                                    disabled={procesando}
                                    className="me-2"
                                >
                                    Cerrar
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleRechazar}
                                    disabled={procesando}
                                    className="me-2"
                                >
                                    <FontAwesomeIcon icon="times" className="me-2" />
                                    Rechazar
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={handleAprobar}
                                    disabled={procesando}
                                >
                                    {procesando ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon="check" className="me-2" />
                                            Aprobar
                                        </>
                                    )}
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={onHide}
                                disabled={procesando}
                            >
                                Cerrar
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal de rechazo */}
            <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Rechazar Solicitud de Vacaciones</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>
                            Motivo del rechazo <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={motivoRechazo}
                            onChange={(e) => setMotivoRechazo(e.target.value)}
                            placeholder="Ingrese el motivo del rechazo..."
                            disabled={procesando}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowRejectModal(false);
                            setMotivoRechazo('');
                        }}
                        disabled={procesando}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmarRechazo}
                        disabled={procesando || !motivoRechazo.trim()}
                    >
                        {procesando ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Procesando...
                            </>
                        ) : (
                            'Confirmar Rechazo'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

VacationDetailsModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    employee: PropTypes.shape({
        idVacacion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        idUsuario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        position: PropTypes.string,
        photo: PropTypes.string,
        department: PropTypes.string,
        categoria: PropTypes.string,
        vacationStart: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        vacationEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        dFechaInicio: PropTypes.string,
        dFechaFin: PropTypes.string,
        daysRequested: PropTypes.number,
        iDiasSolicitados: PropTypes.number,
        estado: PropTypes.string,
        comentarios: PropTypes.string,
        vFechasJson: PropTypes.string,
        tipoUsuario: PropTypes.string
    }),
    onSuccess: PropTypes.func,
    showActions: PropTypes.bool
};

export default VacationDetailsModal;
